// L'EFFACEUR — Panel admin Support Tickets

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { MessageSquare, RefreshCw, AlertCircle, Clock, CheckCircle, Send } from 'lucide-react';
import { API } from '../../App';
import { Button } from '../ui/button';
import EmptyState from '../ui/empty-state';
import { SkeletonCard } from '../ui/loading-skeleton';

const STATUS_COLORS = {
  open: { label: 'Ouvert', class: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/40' },
  in_progress: { label: 'En cours', class: 'bg-orange-500/20 text-orange-400 border-orange-500/40' },
  waiting_customer: { label: 'Attente client', class: 'bg-purple-500/20 text-purple-400 border-purple-500/40' },
  resolved: { label: 'Résolu', class: 'bg-success/20 text-success border-success/40' },
  closed: { label: 'Fermé', class: 'bg-gray-700 text-gray-400 border-gray-600' },
};
const PRIORITY_COLORS = {
  urgent: 'bg-red-500/20 text-red-400 border-red-500/40',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/40',
  normal: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/40',
  low: 'bg-gray-700 text-gray-400 border-gray-600',
};

const SupportTicketsPanel = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('open');
  const [filterPriority, setFilterPriority] = useState('');
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [setStatusOnReply, setSetStatusOnReply] = useState('waiting_customer');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterPriority) params.set('priority', filterPriority);
      const [tRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/support/tickets?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/support/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setTickets(tRes.data.tickets || []);
      setStats(sRes.data);
    } catch (e) {
      toast.error('Erreur chargement tickets');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/support/tickets/${selected.ticket_id}/reply`,
        { message: reply, set_status: setStatusOnReply },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Réponse envoyée au client');
      setReply('');
      setSelected(null);
      fetchData();
    } catch (e) {
      toast.error("Erreur envoi");
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (newStatus) => {
    if (!selected) return;
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/admin/support/tickets/${selected.ticket_id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Ticket ${STATUS_COLORS[newStatus]?.label}`);
      setSelected({ ...selected, status: newStatus });
      fetchData();
    } catch {
      toast.error('Erreur');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-1 h-8 bg-magenta-400 rounded-full" style={{ boxShadow: '0 0 10px rgba(255,0,255,0.8)' }} />
          <h2 className="font-orbitron text-xl font-black text-white">Support Tickets</h2>
          <span className="font-mono text-xs text-magenta-400/60">// SLA · TICKETS</span>
        </div>
        <Button onClick={fetchData} variant="outline" className="text-xs">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Ouverts" value={stats.open} color="cyan" />
          <StatCard label="En cours" value={stats.in_progress} color="orange" />
          <StatCard label="Attente client" value={stats.waiting_customer} color="purple" />
          <StatCard label="Résolus 30j" value={stats.resolved_last_30d} color="success" />
          <StatCard label="SLA dépassé" value={stats.sla_breached} color="red" highlight={stats.sla_breached > 0} />
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {['open', 'in_progress', 'waiting_customer', 'resolved', ''].map(s => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`text-xs px-3 py-1 rounded font-mono ${filterStatus === s ? 'bg-magenta-400 text-black' : 'bg-gray-800 text-gray-400'}`}
          >
            {s ? (STATUS_COLORS[s]?.label || s) : 'Tous'}
          </button>
        ))}
        <span className="text-gray-700">|</span>
        {['urgent', 'high', 'normal', 'low', ''].map(p => (
          <button
            key={p || 'all'}
            onClick={() => setFilterPriority(p)}
            className={`text-xs px-3 py-1 rounded font-mono ${filterPriority === p ? 'bg-cyan-400 text-black' : 'bg-gray-800 text-gray-400'}`}
          >
            {p || 'Toutes prio'}
          </button>
        ))}
      </div>

      {loading ? (
        <SkeletonCard />
      ) : tickets.length === 0 ? (
        <EmptyState icon={MessageSquare} title="Aucun ticket" description="Les nouveaux tickets apparaîtront ici." />
      ) : (
        <div className="space-y-2">
          {tickets.map(t => {
            const sla = t.sla_due_at && new Date(t.sla_due_at) < new Date() && !['resolved', 'closed'].includes(t.status);
            return (
              <div
                key={t.ticket_id}
                onClick={() => setSelected(t)}
                className="glass-card border border-gray-800 hover:border-magenta-400/40 transition-colors p-4 cursor-pointer"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${STATUS_COLORS[t.status]?.class}`}>{STATUS_COLORS[t.status]?.label}</span>
                      <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${PRIORITY_COLORS[t.priority]}`}>{t.priority?.toUpperCase()}</span>
                      {sla && <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-red-500/20 text-red-400 border border-red-500/40">SLA DÉPASSÉ</span>}
                    </div>
                    <div className="text-white font-bold">{t.subject}</div>
                    <div className="text-xs text-gray-400">{t.user_name || t.user_email}</div>
                    <div className="text-xs text-gray-500 mt-1 truncate">{t.messages?.[0]?.body?.slice(0, 100)}</div>
                  </div>
                  <div className="text-[10px] text-gray-500 font-mono text-right">
                    {t.messages?.length || 0} msg<br />
                    {t.created_at?.slice(0, 10)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal détail */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setSelected(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="glass-card border border-magenta-400/40 p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-orbitron text-lg font-bold text-white mb-1">{selected.subject}</h3>
                <div className="text-xs text-gray-500 font-mono">{selected.ticket_id} · {selected.user_email}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-500 hover:text-white text-2xl leading-none">×</button>
            </div>

            <div className="flex gap-2 mb-4 flex-wrap">
              {Object.entries(STATUS_COLORS).map(([key, val]) => (
                <button key={key} onClick={() => updateStatus(key)}
                  className={`text-[10px] font-mono px-2 py-1 rounded border ${selected.status === key ? val.class : 'bg-gray-800 text-gray-500 border-gray-700'}`}>
                  {val.label}
                </button>
              ))}
            </div>

            <div className="space-y-3 mb-4 max-h-96 overflow-y-auto pr-2">
              {(selected.messages || []).map((m, idx) => (
                <div key={idx} className={`p-3 rounded ${m.from === 'admin' ? 'bg-cyan-500/10 border border-cyan-500/30' : 'bg-gray-900/50 border border-gray-800'}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-[10px] font-mono ${m.from === 'admin' ? 'text-cyan-400' : 'text-gray-400'}`}>{m.from === 'admin' ? '🛡️ Admin' : '👤 Client'}</span>
                    <span className="text-[10px] text-gray-600">{new Date(m.created_at).toLocaleString('fr-FR')}</span>
                  </div>
                  <div className="text-sm text-gray-200 whitespace-pre-wrap">{m.body}</div>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-800 pt-4">
              <textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Votre réponse au client..."
                rows={4} className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm focus:border-magenta-400 focus:outline-none" />
              <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                <select value={setStatusOnReply} onChange={(e) => setSetStatusOnReply(e.target.value)}
                  className="text-xs bg-gray-900 border border-gray-700 rounded px-2 py-1 text-gray-300">
                  <option value="waiting_customer">Mettre en attente client</option>
                  <option value="in_progress">En cours</option>
                  <option value="resolved">Résolu</option>
                </select>
                <Button onClick={sendReply} disabled={sending || !reply.trim()} className="bg-magenta-400 text-white hover:bg-magenta-500 font-bold text-sm">
                  <Send className="w-3 h-3 mr-1" /> {sending ? 'Envoi...' : 'Envoyer'}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color, highlight = false }) => {
  const colorMap = {
    cyan: 'text-cyan-400', orange: 'text-orange-400', purple: 'text-purple-400',
    success: 'text-success', red: 'text-red-400',
  };
  return (
    <div className={`glass-card border ${highlight ? 'border-red-500/40' : 'border-gray-800'} p-3`}>
      <div className="text-[10px] text-gray-500 font-mono uppercase">{label}</div>
      <div className={`text-2xl font-black ${colorMap[color]}`}>{value || 0}</div>
    </div>
  );
};

export default SupportTicketsPanel;
